/**
 * Prize Management tab — API hooks (/api/prize-overview-ops).
 *
 * The page must stay fast while fanning out rodeo → events → entries →
 * transactions. Two levels, and the split between them is deliberate:
 *
 *   • useRodeosPrizeOverview — ONE batched request for every rodeo on the page.
 *     Rodeo headline figures are the whole point of the list, so they load up
 *     front. This is NOT an N+1: the backend resolves all rodeos in a fixed
 *     number of bulk queries, so it costs fewer round-trips than the
 *     fetch-per-card-on-expand version it replaced — and the figures are on
 *     screen without the admin having to expand anything.
 *
 *   • useEventPrizeDetail — per-event entries + payment records. Genuinely
 *     expensive and genuinely not needed until an EVENT row is expanded, so it
 *     stays gated on `enabled`. `prize-management.test.tsx` asserts a collapsed
 *     event fetches nothing, because that is what quietly regresses the moment
 *     someone "simplifies" the loading state.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@/api/http-client';
import type {
  PrizeAllocationStatus,
  PrizeExclusionReason,
  PrizeEntryFlag,
} from '@/types/prizeAllocation';

// ─── Response shapes (mirror PrizeOverviewService) ─────────────────────────

export interface PrizeAllocationBreakdown {
  allocation_mode: 'fixed' | 'dynamic';
  draw_finalized: boolean;
  locked_count: number;
  potential_count: number;
  excluded_count: number;
  locked_amount: number;
  potential_amount: number;
  excluded_amount: number;
  projected_total: number;
  excluded_by_reason: Record<PrizeExclusionReason, number>;
  needs_review_count: number;
}

export interface EventPrizeSummary {
  event_id: string;
  event_name: string;
  event_type: string | null;
  entry_fee: number;
  scoring_state: string;
  breakdown: PrizeAllocationBreakdown;
  /**
   * The three prize-display toggles (Contestants / Results / Live). Shipped on
   * the summary so this page can render the switches without a second events
   * fetch — same columns My Rodeos writes.
   */
  display_prize_to_contestants: boolean;
  show_prize_amounts_in_results: boolean;
  show_prize_amounts_before_complete: boolean;
}

export interface RodeoPrizeOverview {
  rodeo: { id: string; name: string; organization_id: string | null };
  events: EventPrizeSummary[];
  totals: {
    locked_amount: number;
    potential_amount: number;
    excluded_amount: number;
    projected_total: number;
    needs_review_count: number;
  };
}

export interface PrizeOverviewEntry {
  registration_id: string;
  user_profile_id: string | null;
  contestant_name: string;
  registration_status: string | null;
  draw_position: number | null;

  status: PrizeAllocationStatus;
  exclusion_reason: PrizeExclusionReason | null;
  flags: PrizeEntryFlag[];
  needs_review: boolean;
  unplaced_reason: string | null;
  drawn_out_reason: string | null;

  amount_due: number;
  amount_paid: number;
  amount_refunded: number;
  credit_applied: number;
  payment_status: string;
  payment_method: string | null;
  fee_breakdown: {
    entry_fee: number;
    transaction_fee: number;
    non_member_fee: number;
    total: number;
  } | null;
  paid_by: { user_id: string; name: string } | null;
  responsible_party: { user_id: string; name: string } | null;
}

/** Matches the backend's EventPrizeComputation. */
export interface EventPrizeComputation {
  event_id: string;
  allocation_mode: 'fixed' | 'dynamic';
  entry_count: number;
  display_to_contestants: boolean;
  effective_adjustments: Array<{
    id: string;
    source: 'org_default' | 'custom';
    adjustment_type: 'addition' | 'subtraction';
    amount: number;
    percentage: number | null;
    reason: string;
    description: string | null;
    is_active: boolean;
  }>;
  calculation: {
    grossPrize: number;
    perEntryDollars: number;
    prizeAfterFixedAdjustments: number;
    total: number;
  };
  prize_rounds: Array<{
    round_number: number;
    round_name: string;
    is_average: boolean;
    total: number;
    places: Array<{ place: number; amount: number }>;
  }>;
}

/** One adjustment, priced against both scenarios. Matches the backend. */
export interface PrizeAdjustmentLine {
  label: string;
  source: 'org_default' | 'custom';
  adjustment_type: 'addition' | 'subtraction';
  is_percentage: boolean;
  value: number;
  locked_delta: number;
  projected_delta: number;
}

export type PerEntryBasis =
  | 'flat'
  | 'percentage_of_entry_fee'
  | 'entry_fee_less_deduction'
  | 'none';

/**
 * The calculated route from entries to prize total, as the engine applies it:
 * entry contributions → dollar adjustments → percentage adjustments → total.
 * Priced twice — locked-only, and the projected pool the payouts come from.
 */
export interface PrizeCalculationTrail {
  allocation_mode: 'fixed' | 'dynamic';
  per_entry_basis: PerEntryBasis;
  per_entry_dollars: number;
  per_entry_value: number;
  entry_fee: number;
  fixed_prize_amount: number;
  locked: { entry_count: number; gross: number; total: number };
  projected: { entry_count: number; gross: number; total: number };
  excluded: { entry_count: number; gross: number };
  adjustments: PrizeAdjustmentLine[];
}

export interface EventPrizeDetail {
  event_id: string;
  event_name: string;
  breakdown: PrizeAllocationBreakdown;
  prize: EventPrizeComputation;
  math: PrizeCalculationTrail;
  entries: PrizeOverviewEntry[];
}

// ─── Query keys ────────────────────────────────────────────────────────────

export const prizeOverviewKeys = {
  all: ['prize-overview'] as const,
  list: (rodeoIds: string[]) => [...prizeOverviewKeys.all, 'list', rodeoIds] as const,
  rodeo: (rodeoId: string) => [...prizeOverviewKeys.all, 'rodeo', rodeoId] as const,
  event: (eventId: string) => [...prizeOverviewKeys.all, 'event', eventId] as const,
};

// ─── Hooks ─────────────────────────────────────────────────────────────────

/**
 * Allocation summaries for MANY rodeos in one request — what the list loads on
 * mount, so every rodeo card shows its locked / potential / not-included
 * figures immediately, collapsed.
 */
export function useRodeosPrizeOverview(
  rodeoIds: string[],
  options?: { enabled?: boolean },
) {
  // Sort so the cache key is stable regardless of the list's incoming order.
  const ids = [...rodeoIds].sort();
  return useQuery({
    queryKey: prizeOverviewKeys.list(ids),
    queryFn: async () => {
      const res = await httpClient.get<{ data: RodeoPrizeOverview[] }>(
        `/prize-overview-ops/rodeos?ids=${encodeURIComponent(ids.join(','))}`,
      );
      return res.data;
    },
    enabled: ids.length > 0 && options?.enabled !== false,
  });
}

/** A single rodeo. Deep-link / refresh path; the list above is the normal one. */
export function useRodeoPrizeOverview(
  rodeoId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: prizeOverviewKeys.rodeo(rodeoId ?? ''),
    queryFn: async () => {
      const res = await httpClient.get<{ data: RodeoPrizeOverview }>(
        `/prize-overview-ops/rodeos/${rodeoId}`,
      );
      return res.data;
    },
    enabled: !!rodeoId && options?.enabled !== false,
  });
}

/**
 * One event's full detail: prize config, allocation breakdown, and every entry
 * with its money. Gate on event-row expansion — this is the expensive call.
 */
export function useEventPrizeDetail(
  eventId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: prizeOverviewKeys.event(eventId ?? ''),
    queryFn: async () => {
      const res = await httpClient.get<{ data: EventPrizeDetail }>(
        `/prize-overview-ops/events/${eventId}`,
      );
      return res.data;
    },
    enabled: !!eventId && options?.enabled !== false,
  });
}

/**
 * Include/exclude an entry from the dynamic prize-pool headcount.
 *
 * Invalidates the whole prize-overview namespace: flipping one contestant moves
 * that event's pool, which moves its rodeo's header totals. Refreshing only the
 * event detail is how the summary row ends up disagreeing with the expanded
 * detail directly beneath it.
 */
export function useSetPrizePoolExclusion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { registrationId: string; excluded: boolean }) => {
      const res = await httpClient.patch<{
        data: { registration_id: string; excluded_from_prize_pool: boolean };
      }>(`/prize-overview-ops/registrations/${vars.registrationId}/prize-pool`, {
        excluded: vars.excluded,
      });
      return res.data;
    },
    onSuccess: () => {
      // Excluding one contestant moves that event's pool, which moves its
      // rodeo's header totals — and those live in the BATCHED list query. Blunt
      // invalidation of the whole namespace is correct here: invalidating only
      // the event key would leave the card above it quoting a stale number.
      queryClient.invalidateQueries({ queryKey: prizeOverviewKeys.all });
    },
  });
}
